import * as z from 'zod/v4';

import { dateTimeSchema } from '../frameworks/entry.js';
import type { Country, CountryId } from './Country.js';
import { eventIdSchema } from './EventId.js';
import { eventStatusEnum } from './EventStatus.js';
import { leagueSchema } from './League.js';
import { type Participant, type ParticipantId, participantTransformSchema } from './Participant.js';

const eventBaseSchema = z.object({
  statusTime: dateTimeSchema,
});

/**
 * Zod schema for Event API response type
 * Used to parse the response from the API into a Event entity
 */
export const eventTransformSchema = z
  .object({
    ...eventBaseSchema.shape,
    matchId: eventIdSchema,
    matchStart: dateTimeSchema,
    sportEventStatus: eventStatusEnum,
    league: leagueSchema,
    participants: z.array(participantTransformSchema.nullish()).nullish(),
    media: z.string().nullish().optional(),
    mutuals: z.string().nullish().optional(),
    result: z.array(
      z
        .object({
          away: z.string(),
          awayPeriod: z.string().optional(),
          description: z.string(),
          home: z.string(),
          homePeriod: z.string().optional(),
          sportEventResultType: z.string(),
          type: z.number(),
        })
        .optional()
    ),
  })
  .transform(({ matchId, matchStart, sportEventStatus, league, participants: participantData, ...match }) => {
    const participantDataArr = (participantData ?? []).filter(
      (participant): participant is z.infer<typeof participantTransformSchema> => participant !== undefined
    );
    const sportEvent = {
      ...match,
      eventId: matchId,
      status: sportEventStatus,
      startDate: matchStart,
      leagueId: league.id,
      participants: participantDataArr.map(({ participant }) => participant.id),
    };

    const { countries, participants } = participantDataArr.reduce(
      (acc, { participant, country }) => {
        acc.countries[country.countryId] = country;
        acc.participants[participant.id] = participant;

        return acc;
      },
      {
        countries: {} as Record<CountryId, Country>,
        participants: {} as Record<ParticipantId, Participant>,
      }
    );

    return {
      participants,
      countries,
      league,
      event: sportEvent,
    };
  });
